
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Switch,
  ScrollView,
  SafeAreaView,
  Modal,
  Pressable,
  Alert,
  TextInput,
  ActivityIndicator,
  Platform,
  StatusBar,
  Linking,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import {
  Ionicons,
  MaterialIcons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
// API
// ═══════════════════════════════════════════════════════════════════════════════
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
// Standalone Settings Service - No external dependencies
const StandaloneSettingsService = {
  // Initialize default preferences with ALL properties
  getDefaultPreferences: () => ({
    // Application preferences
    autoSave: true,
    emailConfirmation: true,
    dataRetention: 30,
    autoBackup: true,
    offlineMode: false,
    
    // Notification preferences
    pushNotifications: true,
    emailNotifications: true,
    smsNotifications: false,
    jobAlerts: true,
    interviewReminders: true,
    applicationUpdates: true,
    marketingEmails: false,
    
    // Appearance preferences
    darkMode: false, // ✅ CRITICAL: This was missing
    language: 'English',
    region: 'United Kingdom',
    currency: 'GBP (£)',
    timeFormat: '24-hour',
    
    // Privacy preferences
    dataSharing: false,
    analytics: true,
    crashReporting: true,
    
    // Additional preferences
    dataRetentionDays: 30,
    enableBiometrics: false,
    twoFactorAuth: false,
    autoSync: true,
    highContrastMode: false,
    fontSize: 'medium',
    vibrateOnNotifications: true,
    soundOnNotifications: true,
    showPreviewInNotifications: true,
    locationSharing: false,
    searchHistory: true,
    cookieConsent: true,
    autoUpdate: true,
    backgroundData: true,
    highQualityMedia: false,
    videoAutoPlay: false,
    reduceMotion: false,
    boldText: false,
    screenReader: false,
  }),

  // Load preferences from AsyncStorage with better error handling
  loadPreferences: async () => {
    try {
      const stored = await AsyncStorage.getItem('userPreferences');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Deep merge with defaults to ensure all properties exist
        const defaults = StandaloneSettingsService.getDefaultPreferences();
        const merged = { ...defaults };
        
        // Copy all properties from stored data
        Object.keys(parsed).forEach(key => {
          if (key in defaults || parsed[key] !== undefined) {
            merged[key] = parsed[key];
          }
        });
        
        return merged;
      }
      return StandaloneSettingsService.getDefaultPreferences();
    } catch (error) {
      console.error('Error loading preferences:', error);
      // Return a fresh copy of defaults
      return { ...StandaloneSettingsService.getDefaultPreferences() };
    }
  },

  // Save preferences to AsyncStorage
  savePreferences: async (preferences) => {
    try {
      // Ensure all default properties are included
      const defaults = StandaloneSettingsService.getDefaultPreferences();
      const completePreferences = { ...defaults, ...preferences };
      await AsyncStorage.setItem('userPreferences', JSON.stringify(completePreferences));
      return true;
    } catch (error) {
      console.error('Error saving preferences:', error);
      return false;
    }
  },

  // Update specific preference with validation
  updatePreference: async (key, value) => {
    try {
      const current = await StandaloneSettingsService.loadPreferences();
      const updated = { ...current, [key]: value };
      await StandaloneSettingsService.savePreferences(updated);
      return updated;
    } catch (error) {
      console.error('Error updating preference:', error);
      return null;
    }
  },

  // Get user data with fallback
  getUserData: async () => {
    try {
      const stored = await AsyncStorage.getItem('userData');
      if (stored) {
        return JSON.parse(stored);
      }
      return {
        full_name: 'Professional User',
        email: 'user@example.com',
        subscription_plan: 'Free',
        phone: '+44 7700 900123',
        location: 'London, UK',
        created_at: new Date().toISOString(),
        last_login: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Error loading user data:', error);
      return {
        full_name: 'Professional User',
        email: 'user@example.com',
        subscription_plan: 'Free',
        created_at: new Date().toISOString(),
      };
    }
  },

  // Export user data
  exportUserData: async () => {
    try {
      const preferences = await StandaloneSettingsService.loadPreferences();
      const userData = await StandaloneSettingsService.getUserData();
      const exportData = {
        userData,
        preferences,
        exportDate: new Date().toISOString(),
        appVersion: '1.2.0',
        platform: Platform.OS,
      };
      return exportData;
    } catch (error) {
      console.error('Error exporting data:', error);
      return null;
    }
  },

  // Clear all data (for account deletion)
  clearAllData: async () => {
    try {
      const keys = await AsyncStorage.getAllKeys();
      await AsyncStorage.multiRemove(keys);
      return true;
    } catch (error) {
      console.error('Error clearing data:', error);
      return false;
    }
  },

  // Reset to default preferences
  resetToDefaults: async () => {
    try {
      const defaults = StandaloneSettingsService.getDefaultPreferences();
      await StandaloneSettingsService.savePreferences(defaults);
      return defaults;
    } catch (error) {
      console.error('Error resetting preferences:', error);
      return null;
    }
  },
};

// Safe preference access helper
const getPreferenceValue = (preferences, key, defaultValue = false) => {
  if (!preferences || typeof preferences !== 'object') {
    return defaultValue;
  }
  return preferences[key] !== undefined ? preferences[key] : defaultValue;
};

export default function SettingsScreen() {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const navigation = useNavigation();

  // State variables for settings
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [preferences, setPreferences] = useState(null); // Start as null
  const [userData, setUserData] = useState(null);

  // Modal states
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showRegionModal, setShowRegionModal] = useState(false);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  // Available options
  const languages = [
    'English', 'Welsh', 'Scottish Gaelic', 'Irish', 'French', 'German', 
    'Spanish', 'Italian', 'Polish', 'Arabic', 'Hindi', 'Urdu', 'Chinese',
    'Portuguese', 'Russian', 'Japanese', 'Korean'
  ];

  const regions = [
    'United Kingdom', 'England', 'Scotland', 'Wales', 'Northern Ireland',
    'European Union', 'United States', 'Canada', 'Australia', 'New Zealand',
    'India', 'Germany', 'France', 'Netherlands', 'Other'
  ];

  const currencies = [
    'GBP (£)', 'USD ($)', 'EUR (€)', 'CAD ($)', 'AUD ($)', 'INR (₹)',
    'JPY (¥)', 'CHF (Fr)', 'SEK (kr)', 'NOK (kr)', 'DKK (kr)'
  ];

  useEffect(() => {
    loadAllSettings();
    
    return () => {
      // Cleanup error handlers if they were set
    };
  }, []);

  const loadAllSettings = async () => {
    try {
      setIsLoading(true);
      const [loadedPreferences, loadedUserData] = await Promise.all([
        StandaloneSettingsService.loadPreferences(),
        StandaloneSettingsService.getUserData(),
      ]);
      
      // Ensure preferences is a proper object
      if (!loadedPreferences || typeof loadedPreferences !== 'object') {
        console.warn('Loaded preferences is invalid, using defaults');
        setPreferences(StandaloneSettingsService.getDefaultPreferences());
      } else {
        setPreferences(loadedPreferences);
      }
      
      setUserData(loadedUserData || {});
    } catch (error) {
      console.error('Error loading settings:', error);
      // Use defaults immediately
      setPreferences(StandaloneSettingsService.getDefaultPreferences());
      setUserData({
        full_name: 'Professional User',
        email: 'user@example.com',
        subscription_plan: 'Free',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updatePreference = async (key, value) => {
    try {
      setIsSaving(true);
      const updated = await StandaloneSettingsService.updatePreference(key, value);
      if (updated) {
        setPreferences(updated);
      } else {
        Alert.alert('Error', 'Failed to save setting. Please try again.');
      }
    } catch (error) {
      console.error('Error updating preference:', error);
      Alert.alert('Error', 'Failed to save setting. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetSettings = async () => {
    try {
      setIsLoading(true);
      const defaults = await StandaloneSettingsService.resetToDefaults();
      if (defaults) {
        setPreferences(defaults);
        Alert.alert('Success', 'All settings have been reset to defaults.');
      } else {
        Alert.alert('Error', 'Failed to reset settings.');
      }
    } catch (error) {
      console.error('Error resetting settings:', error);
      Alert.alert('Error', 'Failed to reset settings.');
    } finally {
      setIsLoading(false);
      setShowResetConfirmation(false);
    }
  };

  const handleLogout = () => {
    Alert.alert(
      'Logout',
      'Are you sure you want to logout? You will need to sign in again to access your account.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Logout',
          style: 'destructive',
          onPress: async () => {
            try {
              await AsyncStorage.removeItem('userToken');
              // Navigate to login screen
              if (navigation.reset) {
                navigation.reset({
                  index: 0,
                  routes: [{ name: 'ProfessionalLoginScreen' }],
                });
              } else {
                navigation.navigate('ProfessionalLoginScreen');
              }
            } catch (error) {
              console.error('Logout error:', error);
              Alert.alert('Error', 'Failed to logout. Please try again.');
            }
          },
        },
      ]
    );
  };

  const handleDeleteAccount = () => {
    if (deleteConfirmation.toLowerCase() === 'delete') {
      Alert.alert(
        'Final Confirmation',
        'This action cannot be undone. All your data, applications, and account information will be permanently deleted.',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete Forever',
            style: 'destructive',
            onPress: async () => {
              try {
                const success = await StandaloneSettingsService.clearAllData();
                setShowDeleteAccountModal(false);
                if (success) {
                  Alert.alert(
                    'Account Deleted',
                    'Your account has been successfully deleted.',
                    [{ text: 'OK', onPress: () => {
                      if (navigation.reset) {
                        navigation.reset({
                          index: 0,
                          routes: [{ name: 'ProfessionalLoginScreen' }],
                        });
                      } else {
                        navigation.navigate('ProfessionalLoginScreen');
                      }
                    }}]
                  );
                } else {
                  Alert.alert('Error', 'Failed to delete account. Please contact support.');
                }
              } catch (error) {
                console.error('Delete account error:', error);
                Alert.alert('Error', 'Failed to delete account. Please contact support.');
              }
            },
          },
        ]
      );
    } else {
      Alert.alert('Error', 'Please type "delete" to confirm account deletion.');
    }
  };

  // Render a setting item with a switch - ULTRA SAFE VERSION
  const renderSwitchSetting = (icon, iconColor, title, description, value, onValueChange) => {
    // Don't render if still loading
    if (isLoading && preferences === null) {
      return (
        <View style={styles.settingItem}>
          <View style={[styles.settingIconContainer, { backgroundColor: iconColor + '20' }]}>
            <Ionicons name={icon} size={22} color={iconColor} />
          </View>
          <View style={styles.settingContent}>
            <Text style={styles.settingTitle}>{title}</Text>
            {description && <Text style={styles.settingDescription}>{description}</Text>}
          </View>
          <ActivityIndicator size={20} color="#64748B" />
        </View>
      );
    }
    
    return (
      <View style={styles.settingItem}>
        <View style={[styles.settingIconContainer, { backgroundColor: iconColor + '20' }]}>
          <Ionicons name={icon} size={22} color={iconColor} />
        </View>
        <View style={styles.settingContent}>
          <Text style={styles.settingTitle}>{title}</Text>
          {description && <Text style={styles.settingDescription}>{description}</Text>}
        </View>
        <Switch
          value={value !== undefined ? value : false}
          onValueChange={onValueChange}
          trackColor={{ false: '#E2E8F0', true: '#0e4e9650' }}
          thumbColor={value ? '#0e4e96' : '#f4f3f4'}
          ios_backgroundColor="#E2E8F0"
          disabled={isSaving}
        />
      </View>
    );
  };

  // Render a setting item with a value display - ULTRA SAFE VERSION
  const renderValueSetting = (icon, iconColor, title, value, onPress) => {
    if (isLoading && preferences === null) {
      return (
        <TouchableOpacity style={styles.settingItem} onPress={onPress} disabled>
          <View style={[styles.settingIconContainer, { backgroundColor: iconColor + '20' }]}>
            <Ionicons name={icon} size={22} color={iconColor} />
          </View>
          <View style={styles.settingContent}>
            <Text style={styles.settingTitle}>{title}</Text>
          </View>
          <View style={styles.settingValueContainer}>
            <ActivityIndicator size={20} color="#64748B" />
          </View>
        </TouchableOpacity>
      );
    }
    
    return (
      <TouchableOpacity style={styles.settingItem} onPress={onPress}>
        <View style={[styles.settingIconContainer, { backgroundColor: iconColor + '20' }]}>
          <Ionicons name={icon} size={22} color={iconColor} />
        </View>
        <View style={styles.settingContent}>
          <Text style={styles.settingTitle}>{title}</Text>
        </View>
        <View style={styles.settingValueContainer}>
          <Text style={styles.settingValue}>{value || 'Not set'}</Text>
          <Ionicons name="chevron-forward" size={20} color="#64748B" />
        </View>
      </TouchableOpacity>
    );
  };

  // Render a modal for selecting options
  const renderSelectionModal = (visible, setVisible, title, options, selectedKey, onSelect) => {
    return (
      <Modal
        visible={visible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{title}</Text>
              <Pressable style={styles.closeButton} onPress={() => setVisible(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </Pressable>
            </View>
            <ScrollView style={styles.modalScrollView}>
              {options.map(option => (
                <TouchableOpacity
                  key={option}
                  style={[
                    styles.modalOption,
                    getPreferenceValue(preferences, selectedKey) === option && styles.modalOptionSelected,
                  ]}
                  onPress={() => {
                    updatePreference(selectedKey, option);
                    setVisible(false);
                  }}
                >
                  <Text
                    style={[
                      styles.modalOptionText,
                      getPreferenceValue(preferences, selectedKey) === option && styles.modalOptionTextSelected,
                    ]}
                  >
                    {option}
                  </Text>
                  {getPreferenceValue(preferences, selectedKey) === option && (
                    <Ionicons name="checkmark" size={20} color="#0e4e96" />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  // Don't render the main UI until preferences are loaded
  if (isLoading || preferences === null) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#0e4e96" />
        <Text style={styles.loadingText}>Loading settings...</Text>
        <Text style={styles.loadingSubText}>Initializing preferences...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Settings</Text>
        <View style={styles.headerRight}>
          {isSaving && <ActivityIndicator size={20} color="#FFFFFF" />}
        </View>
      </View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {/* Account Settings Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Account Settings</Text>

          {renderValueSetting(
            'person-outline',
            '#0e4e96',
            'Profile',
            userData?.full_name || 'View Profile',
            () => {
              Alert.alert('Profile', 'Edit your profile information');
            }
          )}

          {renderValueSetting(
            'shield-checkmark-outline',
            '#0e4e96',
            'Account Type',
            userData?.subscription_plan || 'Free',
            () => {
              Alert.alert('Subscription', 'Manage your subscription and billing settings');
            }
          )}
        </View>

        {/* Application Preferences */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Application Preferences</Text>

          {renderSwitchSetting(
            'save-outline',
            '#10b981',
            'Auto-save Applications',
            'Automatically save application drafts',
            getPreferenceValue(preferences, 'autoSave', true),
            (value) => updatePreference('autoSave', value)
          )}

          {renderSwitchSetting(
            'mail-outline',
            '#10b981',
            'Email Confirmations',
            'Send confirmation emails for applications',
            getPreferenceValue(preferences, 'emailConfirmation', true),
            (value) => updatePreference('emailConfirmation', value)
          )}

          {renderSwitchSetting(
            'cloud-upload-outline',
            '#10b981',
            'Auto Backup',
            'Automatically backup your data',
            getPreferenceValue(preferences, 'autoBackup', true),
            (value) => updatePreference('autoBackup', value)
          )}
        </View>

        {/* Notification Settings Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Notification Settings</Text>

          {renderSwitchSetting(
            'notifications-outline',
            '#3b82f6',
            'Push Notifications',
            'Receive updates on your device',
            getPreferenceValue(preferences, 'pushNotifications', true),
            (value) => updatePreference('pushNotifications', value)
          )}

          {renderSwitchSetting(
            'mail-outline',
            '#3b82f6',
            'Email Notifications',
            'Receive updates via email',
            getPreferenceValue(preferences, 'emailNotifications', true),
            (value) => updatePreference('emailNotifications', value)
          )}

          {renderSwitchSetting(
            'briefcase-outline',
            '#3b82f6',
            'Job Alerts',
            'Get notified about new job opportunities',
            getPreferenceValue(preferences, 'jobAlerts', true),
            (value) => updatePreference('jobAlerts', value)
          )}
        </View>

        {/* Appearance Settings Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Appearance & Localization</Text>

          {renderSwitchSetting(
            'moon-outline',
            '#8b5cf6',
            'Dark Mode',
            'Switch between light and dark themes',
            getPreferenceValue(preferences, 'darkMode', false),
            (value) => updatePreference('darkMode', value)
          )}

          {renderValueSetting(
            'language-outline',
            '#8b5cf6',
            'Language',
            getPreferenceValue(preferences, 'language', 'English'),
            () => setShowLanguageModal(true)
          )}

          {renderValueSetting(
            'location-outline',
            '#8b5cf6',
            'Region',
            getPreferenceValue(preferences, 'region', 'United Kingdom'),
            () => setShowRegionModal(true)
          )}

          {renderValueSetting(
            'cash-outline',
            '#8b5cf6',
            'Currency',
            getPreferenceValue(preferences, 'currency', 'GBP (£)'),
            () => setShowCurrencyModal(true)
          )}
        </View>

        {/* Privacy & Data Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Privacy & Data</Text>

          {renderSwitchSetting(
            'analytics-outline',
            '#0e4e96',
            'Analytics',
            'Help improve the app by sharing usage data',
            getPreferenceValue(preferences, 'analytics', true),
            (value) => updatePreference('analytics', value)
          )}

          {renderSwitchSetting(
            'bug-outline',
            '#0e4e96',
            'Crash Reporting',
            'Automatically send crash reports to help fix issues',
            getPreferenceValue(preferences, 'crashReporting', true),
            (value) => updatePreference('crashReporting', value)
          )}

          {renderValueSetting(
            'download-outline',
            '#0e4e96',
            'Export Data',
            'Download your personal data',
            async () => {
              const exportData = await StandaloneSettingsService.exportUserData();
              if (exportData) {
                Alert.alert(
                  'Data Export',
                  'Your data has been prepared for export. Data includes your profile, preferences, and settings.',
                  [{ text: 'OK' }]
                );
              }
            }
          )}
        </View>

        {/* Account Actions Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Account Actions</Text>

          <TouchableOpacity 
            style={styles.actionButton} 
            onPress={() => setShowResetConfirmation(true)}
          >
            <Ionicons name="refresh-outline" size={20} color="#0e4e96" />
            <Text style={styles.resetButtonText}>Reset All Settings</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.actionButton} 
            onPress={() => setShowDeleteAccountModal(true)}
          >
            <Ionicons name="trash-outline" size={20} color="#ef4444" />
            <Text style={styles.deleteButtonText}>Delete Account</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
            <Ionicons name="log-out-outline" size={20} color="#FFFFFF" />
            <Text style={styles.logoutButtonText}>Logout</Text>
          </TouchableOpacity>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>JOBENG © 2024</Text>
          <Text style={styles.footerSubText}>Settings loaded successfully</Text>
        </View>
      </ScrollView>

      {/* Modals */}
      {renderSelectionModal(
        showLanguageModal,
        setShowLanguageModal,
        'Select Language',
        languages,
        'language'
      )}

      {renderSelectionModal(
        showRegionModal,
        setShowRegionModal,
        'Select Region',
        regions,
        'region'
      )}

      {renderSelectionModal(
        showCurrencyModal,
        setShowCurrencyModal,
        'Select Currency',
        currencies,
        'currency'
      )}

      {/* Reset Confirmation Modal */}
      <Modal
        visible={showResetConfirmation}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowResetConfirmation(false)}
      >
        <View style={styles.confirmModalContainer}>
          <View style={styles.confirmModalContent}>
            <Ionicons name="warning-outline" size={48} color="#f59e0b" />
            <Text style={styles.confirmModalTitle}>Reset Settings?</Text>
            <Text style={styles.confirmModalText}>
              This will reset all your preferences to default values. This action cannot be undone.
            </Text>
            <View style={styles.confirmModalButtons}>
              <TouchableOpacity 
                style={[styles.confirmModalButton, styles.cancelButton]}
                onPress={() => setShowResetConfirmation(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.confirmModalButton, styles.confirmButton]}
                onPress={handleResetSettings}
              >
                <Text style={styles.confirmButtonText}>Reset</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Delete Account Modal */}
      <Modal
        visible={showDeleteAccountModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowDeleteAccountModal(false)}
      >
        <View style={styles.confirmModalContainer}>
          <View style={styles.confirmModalContent}>
            <Ionicons name="alert-circle-outline" size={48} color="#ef4444" />
            <Text style={styles.confirmModalTitle}>Delete Account?</Text>
            <Text style={styles.confirmModalText}>
              This action will permanently delete your account and all associated data. This cannot be undone.
            </Text>
            <Text style={styles.confirmModalWarning}>
              Type "delete" below to confirm:
            </Text>
            <TextInput
              style={styles.deleteInput}
              value={deleteConfirmation}
              onChangeText={setDeleteConfirmation}
              placeholder="Type 'delete'"
              autoCapitalize="none"
            />
            <View style={styles.confirmModalButtons}>
              <TouchableOpacity 
                style={[styles.confirmModalButton, styles.cancelButton]}
                onPress={() => {
                  setShowDeleteAccountModal(false);
                  setDeleteConfirmation('');
                }}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.confirmModalButton, styles.deleteConfirmButton]}
                onPress={handleDeleteAccount}
              >
                <Text style={styles.deleteConfirmButtonText}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    padding: 20,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    fontWeight: '600',
    color: '#0e4e96',
  },
  loadingSubText: {
    marginTop: 8,
    fontSize: 14,
    color: '#64748b',
    textAlign: 'center',
  },
  header: {
    backgroundColor: '#0e4e96',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    paddingTop: Platform.OS === 'ios' ? 16 : StatusBar.currentHeight + 16,
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  headerRight: {
    width: 40,
    alignItems: 'center',
  },
  container: {
    flex: 1,
  },
  section: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 12,
    paddingVertical: 8,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1e293b',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f8fafc',
  },
  settingIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  settingContent: {
    flex: 1,
  },
  settingTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 2,
  },
  settingDescription: {
    fontSize: 14,
    color: '#64748b',
  },
  settingValueContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  settingValue: {
    fontSize: 14,
    color: '#64748b',
    marginRight: 8,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8fafc',
    marginHorizontal: 16,
    marginVertical: 6,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  resetButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0e4e96',
    marginLeft: 8,
  },
  deleteButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ef4444',
    marginLeft: 8,
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0e4e96',
    marginHorizontal: 16,
    marginVertical: 8,
    paddingVertical: 12,
    borderRadius: 8,
  },
  logoutButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  footer: {
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  footerText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748b',
    textAlign: 'center',
  },
  footerSubText: {
    fontSize: 12,
    color: '#94a3b8',
    textAlign: 'center',
    marginTop: 4,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
  },
  closeButton: {
    padding: 4,
  },
  modalScrollView: {
    maxHeight: 400,
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f8fafc',
  },
  modalOptionSelected: {
    backgroundColor: '#f0f9ff',
  },
  modalOptionText: {
    fontSize: 16,
    color: '#1e293b',
  },
  modalOptionTextSelected: {
    fontWeight: '600',
    color: '#0e4e96',
  },
  confirmModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  confirmModalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
  },
  confirmModalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1e293b',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  confirmModalText: {
    fontSize: 16,
    color: '#64748b',
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 22,
  },
  confirmModalWarning: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ef4444',
    textAlign: 'center',
    marginBottom: 8,
  },
  deleteInput: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    marginBottom: 16,
    textAlign: 'center',
  },
  confirmModalButtons: {
    flexDirection: 'row',
    width: '100%',
    gap: 12,
  },
  confirmModalButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: '#f1f5f9',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#64748b',
  },
  confirmButton: {
    backgroundColor: '#0e4e96',
  },
  confirmButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  deleteConfirmButton: {
    backgroundColor: '#ef4444',
  },
  deleteConfirmButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
